import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { DataExplorerColumnHeader } from './DataExplorerColumnHeader';
import { DashboardValueVsExpectedCell } from '../dashboard/rows/cells/base/DashboardValueVsExpectedCell';
import { DataExplorerComparisonTooltip } from './DataExplorerComparisonTooltip';
import { Spinner } from '../Spinner';
import { SORT_DOWN, SORT_UP } from '../../utils/Array.extensions';
import moment from 'moment';
import { getPeriodsData } from '../dashboard/columns';
import { FieldSensitivityService } from '../../utils/FieldSensitivityService';
import { TableExportControls } from './TableExportControls';
import { LocalStorage } from '../../utils/LocalStorage';

class DataExplorerTableComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            columnOrdering: {
                columnId: null,
                direction: SORT_DOWN,
            },
            sensitivities: {}, // Cache de sensibilidades por fieldId
            sensitivitiesLoaded: false, // Flag para saber si las sensibilidades ya se cargaron
            heatMapEnabled: LocalStorage.getHeatMapEnabled(), // Estado del toggle de mapa de calor
            columnWidths: {}, // Anchos de columna personalizados { [columnId]: widthPx }
            resizingColumnId: null, // Columna actualmente siendo redimensionada
        };
        this.handleOrderingChange = this.handleOrderingChange.bind(this);
        this.handleSensitivitySaved = this.handleSensitivitySaved.bind(this);
        this.loadSensitivities = this.loadSensitivities.bind(this);
        this.handleHeatMapToggle = this.handleHeatMapToggle.bind(this);
        this.handleColumnResizeStart = this.handleColumnResizeStart.bind(this);
        this.handleColumnResizeMove = this.handleColumnResizeMove.bind(this);
        this.handleColumnResizeEnd = this.handleColumnResizeEnd.bind(this);
        this._resizeStartX = 0;
        this._resizeStartWidth = 0;
    }

    componentDidMount() {
        // Cargar sensibilidades inmediatamente cuando el componente se monta
        if (this.props.results && this.props.fieldsData) {
            this.loadSensitivities();
        }
    }

    componentDidUpdate(prevProps) {
        // Recargar sensibilidades si cambian los resultados (nuevos campos) o fieldsData
        if (prevProps.results !== this.props.results || prevProps.fieldsData !== this.props.fieldsData) {
            // Resetear el flag para evitar mostrar colores incorrectos mientras se cargan
            this.setState({ sensitivitiesLoaded: false });
            if (this.props.results && this.props.fieldsData) {
                this.loadSensitivities();
            } else {
                // Si no hay datos, marcar como cargado inmediatamente
                this.setState({ sensitivitiesLoaded: true });
            }
        }
        
        // Resetear anchos de columna cuando cambian los resultados (nuevas columnas)
        if (prevProps.results !== this.props.results) {
            this.setState({ columnWidths: {} });
        }

        // Establecer ordenamiento por defecto en la primera columna de dimensión cuando se cargan nuevos datos
        if (prevProps.results !== this.props.results && this.props.results && this.props.results.tableData) {
            const { headers } = this.props.results.tableData;
            const { columnOrdering } = this.state;
            
            // Solo establecer ordenamiento por defecto si no hay uno activo
            if (!columnOrdering.columnId && headers && headers.length > 0) {
                // Buscar la primera columna de dimensión (no métrica)
                const firstDimensionColumn = headers.find(header => !this.isMetricColumn(header.key));
                
                if (firstDimensionColumn) {
                    // Establecer la primera columna de dimensión como ordenamiento por defecto descendente
                    this.setState({
                        columnOrdering: {
                            columnId: firstDimensionColumn.key,
                            direction: SORT_DOWN,
                        }
                    });
                }
            }
        }
    }

    async loadSensitivities() {
        const { results, fieldsData } = this.props;
        if (!results || !results.tableData || !fieldsData) {
            // Si no hay datos aún, marcar como cargado para evitar esperar indefinidamente
            this.setState({ sensitivitiesLoaded: true });
            return;
        }

        const { headers } = results.tableData;
        // Extraer fieldIds de las columnas métricas
        const metricFields = headers
            .filter(header => this.isMetricColumn(header.key))
            .map(header => header.key.replace(/^metric_/, ''));

        if (metricFields.length === 0) {
            // Si no hay métricas, marcar como cargado inmediatamente
            this.setState({ sensitivitiesLoaded: true });
            return;
        }

        // Siempre cargar desde el servidor para asegurar que tenemos las sensibilidades más actualizadas
        // El endpoint POST /field-sensitivities/ implementa la lógica de fallback automáticamente
        // (usuario → agencia → default), así que siempre obtendremos las sensibilidades correctas
        try {
            const sensitivities = await FieldSensitivityService.loadSensitivities(metricFields);
            this.setState({ 
                sensitivities,
                sensitivitiesLoaded: true 
            });
        } catch (error) {
            console.error('Error loading sensitivities:', error);
            // Marcar como cargado incluso si hay error para evitar esperar indefinidamente
            this.setState({ sensitivitiesLoaded: true });
        }
    }

    handleSensitivitySaved() {
        // Limpiar el cache para forzar recarga desde el servidor con las sensibilidades actualizadas
        FieldSensitivityService.clearCache();
        // Resetear el flag para que se recarguen las sensibilidades
        this.setState({ sensitivitiesLoaded: false });
        // Recargar sensibilidades después de guardar
        this.loadSensitivities();
    }


    handleOrderingChange(columnOrdering) {
        this.setState({
            columnOrdering: {
                columnId: columnOrdering.columnId(),
                direction: columnOrdering.orderingDirection(),
            },
        });
    }

    getCellValue(value) {
        // Handle metric values with comparison data
        if (typeof value === 'object' && value !== null && value.currentValue !== undefined) {
            return value.currentValue;
        }
        // Handle formatted value objects (dimensions and metrics without comparison)
        if (typeof value === 'object' && value !== null && value.value !== undefined) {
            return value.value;
        }
        return value;
    }

    getComparisonValue(value) {
        // Handle metric values with comparison data
        if (typeof value === 'object' && value !== null && value.comparisonValue !== undefined) {
            return value.comparisonValue;
        }
        return null;
    }

    /**
     * Traduce los valores del enum de BusinessUnitStatus a textos amigables
     * @param {string} enumValue - Valor del enum (ACTIVE, FINISHED, NOT_STARTED_YET, NOT_ACTIVE)
     * @returns {string} - Texto traducido
     */
    translateBusinessUnitStatus(enumValue) {
        const { t } = this.props;
        if (!enumValue || typeof enumValue !== 'string') {
            return enumValue;
        }
        
        const statusMap = {
            'ACTIVE': t('data_explorer.business_unit_status.active', 'Activo'),
            'FINISHED': t('data_explorer.business_unit_status.finished', 'Finalizado'),
            'NOT_STARTED_YET': t('data_explorer.business_unit_status.not_started_yet', 'No iniciado'),
            'NOT_ACTIVE': t('data_explorer.business_unit_status.not_active', 'Inactivo'),
        };
        
        return statusMap[enumValue.toUpperCase()] || enumValue;
    }

    /**
     * Verifica si el header corresponde al campo BusinessUnitStatus
     * @param {Object} header - Header de la columna
     * @returns {boolean}
     */
    isBusinessUnitStatusField(header) {
        if (!header) return false;
        
        const key = (header.key || '').toLowerCase();
        const label = (header.label || '').toLowerCase();
        
        // Buscar por key o label que contenga referencias a business unit status
        return key.includes('presupuesto_estado');
    }

    getFormattedValue(value, header) {
        // Extract formatted value for display
        // For metrics with comparison, use formattedCurrentValue
        if (typeof value === 'object' && value !== null && value.formattedCurrentValue !== undefined) {
            return value.formattedCurrentValue;
        }
        // For dimensions and metrics without comparison, use formattedValue
        if (typeof value === 'object' && value !== null && value.formattedValue !== undefined) {
            const formattedValue = value.formattedValue;
            // Si es el campo BusinessUnitStatus, traducir el valor
            if (this.isBusinessUnitStatusField(header)) {
                return this.translateBusinessUnitStatus(formattedValue);
            }
            return formattedValue;
        }
        
        // Fallback to raw value if no formatted value exists
        return value;
    }

    renderCellValue(value, header) {
        // Render cell value based on dataType
        const dataType = header?.dataType;
        const formattedValue = this.getFormattedValue(value, header);
        
        if (formattedValue === null || formattedValue === undefined) {
            return '';
        }

        // Handle URL data type
        if (dataType === 'URL') {
            const url = typeof formattedValue === 'string' ? formattedValue : String(formattedValue);
            return (
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                    {url}
                </a>
            );
        }

        // Handle IMAGE data type
        if (dataType === 'IMAGE') {
            const imageUrl = typeof formattedValue === 'string' ? formattedValue : String(formattedValue);
            return (
                <img 
                    src={imageUrl} 
                    alt="" 
                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                    onError={(e) => {
                        // Hide image if it fails to load
                        e.target.style.display = 'none';
                    }}
                />
            );
        }

        // For other types, display formattedValue as string
        return <span>{formattedValue}</span>;
    }

    getFluctuation(value) {
        // Determine fluctuation type based on deltaPercentage
        // Return "up" for positive, "down" for negative (to match Dashboard behavior)
        // If both current and comparison values are zero, return 'neutral'
        if (typeof value === 'object' && value !== null) {
            const currentValue = value.currentValue;
            const comparisonValue = value.comparisonValue;
            
            // If both values are zero, return 'neutral'
            if ((currentValue === 0 || currentValue === null || currentValue === undefined) && 
                (comparisonValue === 0 || comparisonValue === null || comparisonValue === undefined)) {
                return 'neutral';
            }
            
            // Otherwise, use deltaPercentage to determine fluctuation
            if (value.deltaPercentage !== null && value.deltaPercentage !== undefined) {
                if (value.deltaPercentage > 0) return 'up';
                if (value.deltaPercentage < 0) return 'down';
            }
        }
        return 'neutral';
    }

    getIndicatorBehavior(headerKey, headers) {
        // Get indicatorBehavior from the header for the given column key
        const header = headers.find(h => h.key === headerKey);
        return header?.indicatorBehavior;
    }

    getColorClass(deltaPercentage, indicatorBehavior, currentValue, comparisonValue, headerKey) {
        // Determine color class based on deltaPercentage, indicatorBehavior, and sensitivity
        // Returns 'good', 'bad', 'very-good', 'very-bad', or 'normal'
        // Este método controla el color del label de comparación (alertLevel)
        // y es controlado por el toggle individual "Alertas por color activas en esta columna"
        
        if (deltaPercentage === null || deltaPercentage === undefined || indicatorBehavior === undefined) {
            return 'normal'; // Neutral if no indicatorBehavior or no delta
        }

        // Si las sensibilidades aún no se han cargado, usar 'normal' para evitar cambios de color
        // Una vez que se carguen, se aplicarán los colores correctos
        if (!this.state.sensitivitiesLoaded) {
            return 'normal';
        }

        // Si hay sensibilidades configuradas, usarlas
        if (currentValue !== null && currentValue !== undefined && 
            comparisonValue !== null && comparisonValue !== undefined) {
            const fieldId = headerKey ? headerKey.replace(/^metric_/, '') : null;
            if (fieldId && this.state.sensitivities[fieldId]) {
                const sensitivity = this.state.sensitivities[fieldId];
                // Verificar si show_sensitivity_color está activo (toggle individual)
                if (sensitivity.show_sensitivity_color === false) {
                    return 'normal';
                }
                return FieldSensitivityService.getColorClass(
                    currentValue,
                    comparisonValue,
                    indicatorBehavior,
                    sensitivity
                );
            }
        }
        
        // Fallback a la lógica original si no hay sensibilidades configuradas para este field
        const isPositive = deltaPercentage >= 0;
        
        if (indicatorBehavior === 'MORE_IS_BETTER') {
            // Higher values are better
            // Positive delta = good (green), Negative delta = bad (red)
            return isPositive ? 'good' : 'bad';
        } else if (indicatorBehavior === 'LESS_IS_BETTER') {
            // Lower values are better
            // Positive delta = bad (red), Negative delta = good (green)
            return isPositive ? 'bad' : 'good';
        }
        
        return 'normal'; // Default to neutral if indicatorBehavior is unknown
    }

    handleHeatMapToggle(enabled) {
        this.setState({ heatMapEnabled: enabled });
    }

    handleColumnResizeStart(columnId, event) {
        event.preventDefault();
        event.stopPropagation();
        // Obtener el ancho actual de la columna desde el th del DOM
        const th = event.target.closest('th');
        const startWidth = th ? th.offsetWidth : 150;
        this._resizeStartX = event.clientX;
        this._resizeStartWidth = startWidth;
        this.setState({ resizingColumnId: columnId });
        document.addEventListener('mousemove', this.handleColumnResizeMove);
        document.addEventListener('mouseup', this.handleColumnResizeEnd);
    }

    handleColumnResizeMove(event) {
        const { resizingColumnId } = this.state;
        if (!resizingColumnId) return;
        const diff = event.clientX - this._resizeStartX;
        const newWidth = Math.max(80, this._resizeStartWidth + diff);
        this.setState(prevState => ({
            columnWidths: {
                ...prevState.columnWidths,
                [resizingColumnId]: newWidth,
            },
        }));
    }

    handleColumnResizeEnd() {
        this.setState({ resizingColumnId: null });
        document.removeEventListener('mousemove', this.handleColumnResizeMove);
        document.removeEventListener('mouseup', this.handleColumnResizeEnd);
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleColumnResizeMove);
        document.removeEventListener('mouseup', this.handleColumnResizeEnd);
    }

    isMetricColumn(headerKey) {
        // Check if the column is a metric (starts with "metric_")
        return headerKey && headerKey.startsWith('metric_');
    }

    /**
     * Extrae y ordena todos los valores numéricos de una columna para calcular percentiles
     * @param {string} headerKey - La clave del header de la columna
     * @param {Array} rows - Array de filas de datos
     * @returns {Array} - Array de valores ordenados de menor a mayor
     */
    getColumnSortedValues(headerKey, rows) {
        if (!rows || rows.length === 0) {
            return [];
        }

        const values = rows
            .map(row => {
                const value = row[headerKey];
                // Extraer el valor numérico
                if (typeof value === 'object' && value !== null && value.currentValue !== undefined) {
                    return value.currentValue;
                }
                if (typeof value === 'object' && value !== null && value.value !== undefined) {
                    return value.value;
                }
                if (typeof value === 'number') {
                    return value;
                }
                return null;
            })
            .filter(val => val !== null && val !== undefined && !isNaN(val));

        // Ordenar de menor a mayor
        return values.sort((a, b) => a - b);
    }

    /**
     * Determina si un valor está en el top 20%, bottom 20%, o en el 60% intermedio
     * @param {number} value - El valor a evaluar
     * @param {Array} sortedValues - Array de valores ordenados de menor a mayor
     * @param {string} indicatorBehavior - 'MORE_IS_BETTER' o 'LESS_IS_BETTER'
     * @returns {Object} - { category: 'top'|'bottom'|'middle', intensity: 0-1 }
     */
    getValueHeatmapCategory(value, sortedValues, indicatorBehavior) {
        if (!sortedValues || sortedValues.length === 0) {
            return { category: 'middle', intensity: 0 };
        }

        const n = sortedValues.length;
        if (n < 2) {
            return { category: 'middle', intensity: 0 };
        }

        // Si todos los valores son iguales, no hay variación para comparar
        // No aplicar color en este caso
        const minValue = sortedValues[0];
        const maxValue = sortedValues[n - 1];
        if (minValue === maxValue) {
            return { category: 'middle', intensity: 0 };
        }

        // Calcular umbrales del 20% inferior y superior
        const bottomThresholdIndex = Math.ceil(n * 0.2) - 1;
        const topThresholdIndex = Math.floor(n * 0.8);

        const bottomThreshold = sortedValues[Math.max(0, bottomThresholdIndex)];
        const topThreshold = sortedValues[Math.min(n - 1, topThresholdIndex)];

        // Determinar categoría basada en indicatorBehavior
        // MORE_IS_BETTER: valores altos = buenos (verde), valores bajos = malos (rojo)
        // LESS_IS_BETTER: valores bajos = buenos (verde), valores altos = malos (rojo)

        if (indicatorBehavior === 'MORE_IS_BETTER') {
            if (value >= topThreshold) {
                // Top 20% - buenos (verde)
                const topValues = sortedValues.filter(v => v >= topThreshold);
                const minTop = Math.min(...topValues);
                const maxTop = Math.max(...topValues);
                const intensity = maxTop === minTop ? 1 : (value - minTop) / (maxTop - minTop);
                return { category: 'top', intensity };
            } else if (value <= bottomThreshold) {
                // Bottom 20% - malos (rojo)
                const bottomValues = sortedValues.filter(v => v <= bottomThreshold);
                const minBottom = Math.min(...bottomValues);
                const maxBottom = Math.max(...bottomValues);
                // Invertir intensidad: valor más bajo = más intenso
                const intensity = maxBottom === minBottom ? 1 : 1 - (value - minBottom) / (maxBottom - minBottom);
                return { category: 'bottom', intensity };
            }
        } else if (indicatorBehavior === 'LESS_IS_BETTER') {
            if (value <= bottomThreshold) {
                // Bottom 20% (valores bajos) - buenos (verde)
                const bottomValues = sortedValues.filter(v => v <= bottomThreshold);
                const minBottom = Math.min(...bottomValues);
                const maxBottom = Math.max(...bottomValues);
                // Invertir intensidad: valor más bajo = más intenso
                const intensity = maxBottom === minBottom ? 1 : 1 - (value - minBottom) / (maxBottom - minBottom);
                return { category: 'top', intensity };
            } else if (value >= topThreshold) {
                // Top 20% (valores altos) - malos (rojo)
                const topValues = sortedValues.filter(v => v >= topThreshold);
                const minTop = Math.min(...topValues);
                const maxTop = Math.max(...topValues);
                const intensity = maxTop === minTop ? 1 : (value - minTop) / (maxTop - minTop);
                return { category: 'bottom', intensity };
            }
        }

        // 60% intermedio - sin color
        return { category: 'middle', intensity: 0 };
    }

    /**
     * Genera el color de fondo RGB basado en la categoría e intensidad
     * Top 20% (buenos) = verde, Bottom 20% (malos) = rojo, Middle 60% = transparente
     * @param {string} category - 'top', 'bottom', o 'middle'
     * @param {number} intensity - Intensidad entre 0 y 1
     * @returns {string} - Color RGB en formato rgba
     */
    getBackgroundColor(category, intensity) {
        if (category === 'middle') {
            return 'transparent';
        }

        if (category === 'top') {
            // Verde: de claro (intensidad 0) a oscuro (intensidad 1)
            const baseColor = { r: 200, g: 255, b: 200 };
            const maxColor = { r: 34, g: 139, b: 34 };
            const baseAlpha = 0.4;
            const maxAlpha = 0.5;

            const r = Math.round(baseColor.r + (maxColor.r - baseColor.r) * intensity);
            const g = Math.round(baseColor.g + (maxColor.g - baseColor.g) * intensity);
            const b = Math.round(baseColor.b + (maxColor.b - baseColor.b) * intensity);
            const alpha = baseAlpha + (maxAlpha - baseAlpha) * intensity;

            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        if (category === 'bottom') {
            // Rojo: de claro (intensidad 0) a oscuro (intensidad 1)
            const baseColor = { r: 255, g: 200, b: 200 };
            const maxColor = { r: 220, g: 53, b: 69 };
            const baseAlpha = 0.4;
            const maxAlpha = 0.5;

            const r = Math.round(baseColor.r + (maxColor.r - baseColor.r) * intensity);
            const g = Math.round(baseColor.g + (maxColor.g - baseColor.g) * intensity);
            const b = Math.round(baseColor.b + (maxColor.b - baseColor.b) * intensity);
            const alpha = baseAlpha + (maxAlpha - baseAlpha) * intensity;

            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        return 'transparent';
    }

    /**
     * Obtiene el estilo de fondo para una celda basado en formato condicional
     * @param {*} value - Valor de la celda
     * @param {string} headerKey - Clave del header
     * @param {Array} headers - Array de headers
     * @param {Array} rows - Array de todas las filas
     * @returns {Object} - Objeto de estilo para aplicar
     *
     * NOTA: Este método solo es controlado por el toggle global "Mostrar mapa de calor en cada columna".
     * El toggle individual "Alertas por color activas en esta columna" NO afecta este método,
     * solo afecta el color del label de comparación (getColorClass).
     *
     * Lógica de mapa de calor:
     * - Top 20% de cada columna → tonos de verde (mejor valor = más intenso)
     * - Bottom 20% de cada columna → tonos de rojo (peor valor = más intenso)
     * - 60% intermedio → sin color (transparente)
     *
     * Se respeta indicatorBehavior:
     * - MORE_IS_BETTER: valores altos = buenos (verde), valores bajos = malos (rojo)
     * - LESS_IS_BETTER: valores bajos = buenos (verde), valores altos = malos (rojo)
     */
    getConditionalFormattingStyle(value, headerKey, headers, rows) {
        if (!this.isMetricColumn(headerKey)) {
            return {};
        }

        // Verificar el toggle global de mapa de calor
        // Si está desactivado, no aplicar formato condicional (fondo de color)
        if (!this.state.heatMapEnabled) {
            return {};
        }

        // Verificar que haya sensibilidad configurada para este field
        // (necesaria para calcular el formato condicional)
        const fieldId = headerKey ? headerKey.replace(/^metric_/, '') : null;
        if (!fieldId || !this.state.sensitivities[fieldId]) {
            // Si no hay sensibilidad configurada, no aplicar formato condicional
            return {};
        }

        // Obtener el valor numérico
        let numericValue = null;
        if (typeof value === 'object' && value !== null && value.currentValue !== undefined) {
            numericValue = value.currentValue;
        } else if (typeof value === 'object' && value !== null && value.value !== undefined) {
            numericValue = value.value;
        } else if (typeof value === 'number') {
            numericValue = value;
        }

        if (numericValue === null || numericValue === undefined || isNaN(numericValue)) {
            return {};
        }

        // Obtener indicatorBehavior del header
        const header = headers.find(h => h.key === headerKey);
        const indicatorBehavior = header?.indicatorBehavior;

        if (!indicatorBehavior) {
            return {};
        }

        // Obtener valores ordenados para esta columna
        const sortedValues = this.getColumnSortedValues(headerKey, rows);

        // Determinar categoría e intensidad basado en percentiles
        const { category, intensity } = this.getValueHeatmapCategory(
            numericValue, sortedValues, indicatorBehavior
        );

        // Generar color de fondo
        const backgroundColor = this.getBackgroundColor(category, intensity);

        return {
            backgroundColor: backgroundColor || 'transparent',
        };
    }

    formatPeriodLabel(dateRangeInfo, periodType) {
        // Format period label from dateRangeInfo
        // periodType can be 'main' or 'comparison'
        const { t } = this.props;
        if (!dateRangeInfo || !dateRangeInfo[periodType]) {
            return '';
        }

        const period = dateRangeInfo[periodType];
        if (period.predefinedPeriod) {
            // Use translated predefined period name
            const periodsData = getPeriodsData(t);
            return periodsData[period.predefinedPeriod] || period.predefinedPeriod;
        }

        // Format date range: "DD/MM/YYYY - DD/MM/YYYY"
        const startDate = moment(period.startDate).format('DD/MM/YYYY');
        const endDate = moment(period.endDate).format('DD/MM/YYYY');
        return `${startDate} - ${endDate}`;
    }

    formatPeriodValue(value) {
        // Format the period value for display in tooltip
        if (value === null || value === undefined) {
            return '-';
        }
        // Format numbers with appropriate decimal places
        if (typeof value === 'number') {
            // If it's a whole number, display without decimals
            if (value % 1 === 0) {
                return value.toString();
            }
            // Otherwise format with 2 decimal places
            return value.toFixed(2);
        }
        return value.toString();
    }

    getSummaryValueForColumn(headerKey, summary) {
        // Extract fieldId from header key (e.g., "metric_ventas" -> "ventas")
        if (!summary || !headerKey) return null;
        
        // Only process metric columns
        if (!this.isMetricColumn(headerKey)) return null;
        
        // Extract fieldId from key (remove "metric_" prefix)
        const fieldId = headerKey.replace(/^metric_/, '');
        
        // Try to find the summary value by fieldId
        let summaryValue = summary[fieldId];
        
        // If not found, try case-insensitive search
        if (!summaryValue) {
            const summaryKeys = Object.keys(summary);
            const matchingKey = summaryKeys.find(key => 
                key.toLowerCase() === fieldId.toLowerCase()
            );
            if (matchingKey) {
                summaryValue = summary[matchingKey];
            }
        }
        
        return summaryValue || null;
    }

    renderSummaryRow(headers, summary, dateRangeInfo, rows) {
        if (!summary || Object.keys(summary).length === 0) {
            return null;
        }

        const { t } = this.props;

        return (
            <tr key="summary-row" className="data-explorer-summary-row">
                {headers.map((header, cellIndex) => {
                    const isMetricColumn = this.isMetricColumn(header.key);
                    const summaryValue = this.getSummaryValueForColumn(header.key, summary);
                    const columnClass = isMetricColumn ? 'data-explorer-metric-column' : 'data-explorer-dimension-column';
                    
                    // Obtener estilo de formato condicional para la fila de resumen
                    const conditionalStyle = this.getConditionalFormattingStyle(summaryValue, header.key, headers, rows);
                    
                    if (isMetricColumn && summaryValue) {
                        // Check if summary has comparison data
                        const hasComparison = summaryValue.currentValue !== undefined || summaryValue.comparisonValue !== undefined;
                        
                        if (hasComparison) {
                            // Render metric with comparison data
                            const comparisonValue = summaryValue.comparisonValue;
                            const currentValue = summaryValue.currentValue;
                            
                            // Si no hay valor principal (null o undefined), no mostrar comparación
                            // Renderizar como métrica simple sin comparación
                            if (currentValue === null || currentValue === undefined) {
                                // Check if it has formattedValue structure
                                if (summaryValue.formattedValue !== undefined) {
                                    return (
                                        <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                            {this.renderCellValue(summaryValue, header)}
                                        </td>
                                    );
                                } else {
                                    // Direct value (backward compatibility)
                                    return (
                                        <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                            {summaryValue !== null && summaryValue !== undefined ? summaryValue : ''}
                                        </td>
                                    );
                                }
                            }
                            
                            const formattedCurrentValue = summaryValue.formattedCurrentValue || currentValue;
                            const fluctuation = this.getFluctuation(summaryValue);
                            
                            // Get indicatorBehavior from summary or header
                            const indicatorBehavior = summaryValue.indicatorBehavior || this.getIndicatorBehavior(header.key, headers);
                            const colorClass = this.getColorClass(
                                summaryValue.deltaPercentage, 
                                indicatorBehavior,
                                currentValue,
                                comparisonValue,
                                header.key
                            );
                            
                            // Format delta percentage
                            let formattedDelta = summaryValue.formattedDelta || '';
                            if (currentValue === 0 && 
                                (comparisonValue === 0 || comparisonValue === null || comparisonValue === undefined)) {
                                formattedDelta = '0%';
                            } else {
                                // Remove the sign (+ or -) from formattedDelta
                                if (formattedDelta.startsWith('+')) {
                                    formattedDelta = formattedDelta.substring(1);
                                } else if (formattedDelta.startsWith('-')) {
                                    formattedDelta = formattedDelta.substring(1);
                                }
                                
                                // Convert percentage to integer
                                if (formattedDelta && formattedDelta.includes('%')) {
                                    const percentageMatch = formattedDelta.match(/(\d+\.?\d*)/);
                                    if (percentageMatch) {
                                        const percentageValue = parseFloat(percentageMatch[1]);
                                        const roundedValue = Math.round(percentageValue);
                                        formattedDelta = `${roundedValue}%`;
                                    }
                                }
                            }
                            
                            // Prepare tooltip data
                            const mainPeriodLabel = this.formatPeriodLabel(dateRangeInfo, 'main');
                            const comparisonPeriodLabel = dateRangeInfo.comparison 
                                ? this.formatPeriodLabel(dateRangeInfo, 'comparison')
                                : '';
                            const mainPeriodValue = summaryValue.formattedCurrentValue || this.formatPeriodValue(currentValue);
                            const comparisonPeriodValue = summaryValue.formattedComparisonValue || this.formatPeriodValue(comparisonValue);

                            return (
                                    <DashboardValueVsExpectedCell
                                        className={columnClass}
                                        style={conditionalStyle}
                                        value={formattedCurrentValue !== null && formattedCurrentValue !== undefined ? formattedCurrentValue : currentValue}
                                        expectedValue={comparisonValue}
                                        fluctuation={fluctuation}
                                        fluctuationPercentage={formattedDelta}
                                        alertLevel={colorClass}
                                        dataExplorerTooltipComponent={DataExplorerComparisonTooltip}
                                        mainPeriodValue={mainPeriodValue !== null && mainPeriodValue !== undefined ? mainPeriodValue : this.formatPeriodValue(currentValue)}
                                        comparisonPeriodValue={comparisonPeriodValue !== null && comparisonPeriodValue !== undefined ? comparisonPeriodValue : this.formatPeriodValue(comparisonValue)}
                                        mainPeriodLabel={mainPeriodLabel}
                                        comparisonPeriodLabel={comparisonPeriodLabel}
                                        metricName={header.label}
                                    />
                            );
                        } else {
                            // Render metric without comparison
                            // Check if it has formattedValue structure
                            if (summaryValue.formattedValue !== undefined) {
                                return (
                                    <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                        {this.renderCellValue(summaryValue, header)}
                                    </td>
                                );
                            } else {
                                // Direct value (backward compatibility)
                                return (
                                    <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                        {summaryValue !== null && summaryValue !== undefined ? summaryValue : ''}
                                    </td>
                                );
                            }
                        }
                    }
                    
                    // For dimension columns, show "Total" label or empty
                    if (cellIndex === 0) {
                        return (
                            <td key={cellIndex} className={columnClass} style={{ fontWeight: 'bold' }}>
                                {t('data_explorer.total', 'Total')}
                            </td>
                        );
                    }
                    
                    return <td key={cellIndex} className={columnClass}></td>;
                })}
            </tr>
        );
    }

    render() {
        const { t, loading, results } = this.props;

        if (loading) {
            return <Spinner />;
        }

        if (!results) {
            return <p>{t('data_explorer.initial_message')}</p>;
        }

        if (!results.tableData || !results.tableData.rows || results.tableData.rows.length === 0) {
            return <p>{t('data_explorer.no_data')}</p>;
        }

        const { headers, rows } = results.tableData;
        const { columnOrdering } = this.state;
        const dateRangeInfo = results.dateRangeInfo || {};
        const summary = results.summary || {};

        // Sort rows based on column ordering
        let sortedRows = [...rows];
        if (columnOrdering.columnId) {
            const headerIndex = headers.findIndex(h => h.key === columnOrdering.columnId);
            if (headerIndex !== -1) {
                const header = headers[headerIndex];
                sortedRows.sort((a, b) => {
                    const valA = this.getCellValue(a[header.key]);
                    const valB = this.getCellValue(b[header.key]);
                    
                    // Handle null values
                    if (valA === null || valA === undefined) return 1;
                    if (valB === null || valB === undefined) return -1;
                    
                    // Handle date values - check if header has dataType DATE or if value looks like a date
                    const isDateColumn = header.dataType === 'DATE' || 
                                        (typeof valA === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(valA)) ||
                                        (typeof valB === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(valB));
                    
                    let comparisonResult = 0;
                    
                    if (isDateColumn) {
                        // Parse dates in DD/MM/YYYY format
                        const dateA = moment(valA, 'DD/MM/YYYY');
                        const dateB = moment(valB, 'DD/MM/YYYY');
                        
                        if (dateA.isValid() && dateB.isValid()) {
                            if (dateA.isBefore(dateB)) {
                                comparisonResult = -1;
                            } else if (dateA.isAfter(dateB)) {
                                comparisonResult = 1;
                            } else {
                                comparisonResult = 0;
                            }
                        } else {
                            // Fallback to string comparison if dates are invalid
                            comparisonResult = valA < valB ? -1 : (valA > valB ? 1 : 0);
                        }
                    } else {
                        // Compare values normally
                        if (valA < valB) {
                            comparisonResult = -1;
                        } else if (valA > valB) {
                            comparisonResult = 1;
                        } else {
                            comparisonResult = 0;
                        }
                    }
                    
                    // Apply sort direction
                    return columnOrdering.direction === SORT_UP ? comparisonResult : -comparisonResult;
                });
            }
        }

        const hasData = results && results.tableData && results.tableData.rows && results.tableData.rows.length > 0;
        const { columnWidths, resizingColumnId } = this.state;
        const hasCustomWidths = Object.keys(columnWidths).length > 0;
        const tableLayoutStyle = hasCustomWidths ? { tableLayout: 'fixed' } : {};

        return (
            <>
                {/* Controles de exportar */}
                {hasData && (
                    <TableExportControls
                        onHeatMapToggle={this.handleHeatMapToggle}
                    />
                )}
                <div className="table-box data-explorer-table-container">
                    <table className={`table ${!this.state.heatMapEnabled ? 'table-striped' : 'heatmap-active'} data-explorer-table`} style={tableLayoutStyle}>
                    <thead>
                        <tr>
                            {headers.map(header => {
                                const isMetricColumn = this.isMetricColumn(header.key);
                                return (
                                    <DataExplorerColumnHeader
                                        key={header.key}
                                        columnId={header.key}
                                        title={header.label}
                                        canBeSorted={true}
                                        onSetOrdering={this.handleOrderingChange}
                                        isActive={columnOrdering.columnId === header.key}
                                        defaultOrderingDirection={columnOrdering.direction}
                                        fieldsData={this.props.fieldsData}
                                        onSensitivitySaved={this.handleSensitivitySaved}
                                        isMetricColumn={isMetricColumn}
                                        columnWidth={columnWidths[header.key]}
                                        onResizeStart={this.handleColumnResizeStart}
                                        isResizing={resizingColumnId === header.key}
                                    />
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((row, rowIndex) => (
                            <tr 
                                key={rowIndex}
                                style={this.state.heatMapEnabled ? { borderBottom: '1px solid #dee2e6' } : {}}
                            >
                                {headers.map((header, cellIndex) => {
                                    const value = row[header.key];
                                    const isMetricColumn = this.isMetricColumn(header.key);
                                    const isMetricWithComparison = typeof value === 'object' && value !== null && value.currentValue !== undefined;
                                    const columnClass = isMetricColumn ? 'data-explorer-metric-column' : 'data-explorer-dimension-column';
                                    
                                    if (isMetricColumn && isMetricWithComparison) {
                                        // Render metric with comparison data
                                        const comparisonValue = this.getComparisonValue(value);
                                        const currentValue = value.currentValue;
                                        
                                        // Si no hay valor principal (null o undefined), no mostrar comparación
                                        // Renderizar como métrica simple sin comparación
                                        if (currentValue === null || currentValue === undefined) {
                                            const conditionalStyle = this.getConditionalFormattingStyle(value, header.key, headers, sortedRows);
                                            return (
                                                <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                                    {this.renderCellValue(value, header)}
                                                </td>
                                            );
                                        }
                                        
                                        const formattedCurrentValue = value.formattedCurrentValue || currentValue;
                                        const fluctuation = this.getFluctuation(value); // Used for arrow direction (up/down)
                                        
                                        // Get indicatorBehavior from header to determine color
                                        const indicatorBehavior = this.getIndicatorBehavior(header.key, headers);
                                        const colorClass = this.getColorClass(
                                            value.deltaPercentage, 
                                            indicatorBehavior,
                                            currentValue,
                                            comparisonValue,
                                            header.key
                                        ); // Used for color (good/bad/very-good/very-bad)
                                        
                                        // Obtener estilo de formato condicional
                                        const conditionalStyle = this.getConditionalFormattingStyle(value, header.key, headers, sortedRows);
                                        
                                        // If both current and comparison values are zero, show "0%"
                                        let formattedDelta = value.formattedDelta || '';
                                        if (currentValue === 0 && 
                                            (comparisonValue === 0 || comparisonValue === null || comparisonValue === undefined)) {
                                            formattedDelta = '0%';
                                        } else {
                                            // Remove the sign (+ or -) from formattedDelta, as the arrow will indicate direction
                                            if (formattedDelta.startsWith('+')) {
                                                formattedDelta = formattedDelta.substring(1);
                                            } else if (formattedDelta.startsWith('-')) {
                                                formattedDelta = formattedDelta.substring(1);
                                            }
                                            
                                            // Convert percentage to integer (remove decimals)
                                            if (formattedDelta && formattedDelta.includes('%')) {
                                                const percentageMatch = formattedDelta.match(/(\d+\.?\d*)/);
                                                if (percentageMatch) {
                                                    const percentageValue = parseFloat(percentageMatch[1]);
                                                    const roundedValue = Math.round(percentageValue);
                                                    formattedDelta = `${roundedValue}%`;
                                                }
                                            }
                                        }
                                        
                                        // Prepare tooltip data - use formatted values for display
                                        const mainPeriodLabel = this.formatPeriodLabel(dateRangeInfo, 'main');
                                        const comparisonPeriodLabel = dateRangeInfo.comparison 
                                            ? this.formatPeriodLabel(dateRangeInfo, 'comparison')
                                            : '';
                                        const mainPeriodValue = value.formattedCurrentValue || this.formatPeriodValue(currentValue);
                                        const comparisonPeriodValue = value.formattedComparisonValue || this.formatPeriodValue(comparisonValue);

                                        return (
                                                <DashboardValueVsExpectedCell
                                                    className={columnClass}
                                                    style={conditionalStyle}
                                                    value={formattedCurrentValue !== null && formattedCurrentValue !== undefined ? formattedCurrentValue : currentValue}
                                                    expectedValue={comparisonValue}
                                                    fluctuation={fluctuation} // Keep for arrow direction (up/down)
                                                    fluctuationPercentage={formattedDelta}
                                                    alertLevel={colorClass} // Use colorClass for the color (good/bad/normal)
                                                    dataExplorerTooltipComponent={DataExplorerComparisonTooltip}
                                                    mainPeriodValue={mainPeriodValue !== null && mainPeriodValue !== undefined ? mainPeriodValue : this.formatPeriodValue(currentValue)}
                                                    comparisonPeriodValue={comparisonPeriodValue !== null && comparisonPeriodValue !== undefined ? comparisonPeriodValue : this.formatPeriodValue(comparisonValue)}
                                                    mainPeriodLabel={mainPeriodLabel}
                                                    comparisonPeriodLabel={comparisonPeriodLabel}
                                                    metricName={header.label}
                                                />
                                        );
                                    }
                                    
                                    // Render dimension or simple metric value (without comparison)
                                    // Use renderCellValue to handle dataType (URL, IMAGE, etc.)
                                    const conditionalStyle = this.getConditionalFormattingStyle(value, header.key, headers, sortedRows);
                                    return (
                                        <td key={cellIndex} className={columnClass} style={conditionalStyle}>
                                            {this.renderCellValue(value, header)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {this.renderSummaryRow(headers, summary, dateRangeInfo, sortedRows)}
                    </tbody>
                </table>
                </div>
            </>
        );
    }
}

export const DataExplorerTable = withTranslation()(DataExplorerTableComponent);
